import { AssetTypes } from "@/model/AssetTypes";
import { ObjectId } from "@/model/db/General";
import InvoiceStruct, {
  ModeOfPaymentWithUnit,
} from "@/redux/actions/struct/implemented/InvoiceStruct";
import ServiceUtils from "@/services/ServiceUtils";
import SubmitService from "@/services/SubmitService";
import { HTTP } from "@/utils/Http";
import {
  InvoiceAccount,
  InvoiceAccountBU,
  InvoiceAccountBUData,
  InvoiceAccountData,
  InvoiceDirection,
} from "../invoiceApp/RAInterfaces";
import {
  DeclineReasonConfig,
  DeclineReasonConfigFormValue,
  ModeOfPayment,
  RACAccountBUImportFormData,
  RACAccountImportFormData,
  RACBookingReasonData,
} from "./RACInterfaces";

class RACServiceClass {
  private transformDeclineReasonConfigFormValue(
    declineReasonFormValue: DeclineReasonConfigFormValue
  ): DeclineReasonConfig {
    const { assignments, type, ...other } = declineReasonFormValue;
    const declineReason: DeclineReasonConfig = {
      ...other,
      assignment: [],
    };
    if (assignments.assignees.active) {
      declineReason.assignment.push({
        type: "previousAssignee",
      });
    }
    if (assignments.teams.active) {
      declineReason.assignment.push({
        type: "team",
        id: assignments.teams.ids,
      });
    }
    if (assignments.users.active) {
      declineReason.assignment.push({
        type: "user",
        id: assignments.users.ids,
      });
    }

    return declineReason;
  }
  async createDeclineReasonConfig(
    type: string,
    direction: InvoiceDirection,
    declineReasonFormValue: DeclineReasonConfigFormValue
  ) {
    return await ServiceUtils.toastError(async () => {
      const declineReasons: DeclineReasonConfig[] =
        InvoiceStruct.getDeclineReasons(direction, [type])?.map(
          ({ type, ...reason }) =>
            reason.id === declineReasonFormValue.id
              ? this.transformDeclineReasonConfigFormValue(
                  declineReasonFormValue
                )
              : reason
        ) || [];
      declineReasons.push(
        this.transformDeclineReasonConfigFormValue(declineReasonFormValue)
      );
      return await this.updateDeclineReasonConfigs(
        type,
        direction,
        declineReasons
      );
    });
  }
  async updateDeclineReasonConfig(
    type: string,
    direction: InvoiceDirection,
    declineReasonFormValue: DeclineReasonConfigFormValue
  ) {
    return await ServiceUtils.toastError(async () => {
      const declineReasons: DeclineReasonConfig[] = (
        InvoiceStruct.getDeclineReasons(direction, [type]) || []
      ).map(({ type, ...reason }) =>
        reason.id === declineReasonFormValue.id
          ? this.transformDeclineReasonConfigFormValue(declineReasonFormValue)
          : reason
      );

      return await this.updateDeclineReasonConfigs(
        type,
        direction,
        declineReasons
      );
    });
  }
  async updateDeclineReasonConfigs(
    type: string,
    direction: InvoiceDirection,
    declineReason: DeclineReasonConfig[]
  ) {
    return await ServiceUtils.toastError(async () => {
      const config = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/updateInvoiceDeclineReason`,
        bodyParams: {
          type,
          direction,
          data: declineReason,
        },
      });

      const data = InvoiceStruct.getData(type);
      data.invoiceConfig = config;
      InvoiceStruct.updateData(type, data);

      return config;
    });
  }

  async createNewModeOfPayment(
    type: string,
    direction: InvoiceDirection,
    payment: ModeOfPaymentWithUnit
  ) {
    return await ServiceUtils.toastError(async () => {
      const payments: ModeOfPaymentWithUnit[] =
        InvoiceStruct.getModalOfPayments(direction, [type]) || [];
      payments.push(payment);
      return await this.updateModeOfPayments(type, direction, payments);
    });
  }
  async updateModeOfPayment(
    type: string,
    direction: InvoiceDirection,
    payment: ModeOfPaymentWithUnit
  ) {
    return await ServiceUtils.toastError(async () => {
      const payments: ModeOfPayment[] = (
        InvoiceStruct.getModalOfPayments(direction, [type]) || []
      ).map((e) => (e.id === payment.id ? payment : e));

      return await this.updateModeOfPayments(type, direction, payments);
    });
  }
  async updateModeOfPayments(
    type: string,
    direction: InvoiceDirection,
    payments: ModeOfPaymentWithUnit[]
  ) {
    return await ServiceUtils.toastError(async () => {
      const config = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/${type}/updateModeOfPayments`,
        bodyParams: {
          direction,
          data: payments.map(({ unit, ...other }) => other),
        },
      });

      const data = InvoiceStruct.getData(type);
      data.invoiceConfig = config;
      InvoiceStruct.updateData(type, data);

      return config;
    });
  }

  async createInvoiceAccount(data: InvoiceAccountData) {
    return await ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/createInvoiceAccount`,
        bodyParams: data,
      });
      return result;
    });
  }
  async updateInvoiceAccount(id: string, data: InvoiceAccountData) {
    return await ServiceUtils.toastError(async () => {
      const { entity, ...sumbitData } = data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/${id}/updateInvoiceAccount`,
        bodyParams: sumbitData,
      });
      return result;
    });
  }
  async archiveInvoiceAccount(account: InvoiceAccount) {
    return await ServiceUtils.toastError(async () => {
      const { entity, ...sumbitData } = account.data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/${account._id}/updateInvoiceAccount`,
        bodyParams: { ...sumbitData, status: "archived" },
      });
      return result;
    });
  }
  async createInvoiceAccountBU(data: InvoiceAccountBUData) {
    return await ServiceUtils.toastError(async () => {
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/createInvoiceAccountBU`,
        bodyParams: data,
      });
      return result;
    });
  }

  async updateInvoiceAccountBU(id: string, data: InvoiceAccountBUData) {
    return await ServiceUtils.toastError(async () => {
      const { entity, ...sumbitData } = data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/${id}/updateInvoiceAccountBU`,
        bodyParams: sumbitData,
      });
      return result;
    });
  }
  async archiveInvoiceAccountBU(account: InvoiceAccountBU) {
    return await ServiceUtils.toastError(async () => {
      const { entity, ...sumbitData } = account.data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/${account._id}/updateInvoiceAccountBU`,
        bodyParams: { ...sumbitData, status: "archived" },
      });
      return result;
    });
  }

  async importAccounts(data: RACAccountImportFormData) {
    return await ServiceUtils.toastError(async () => {
      const { ...body } = data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/importInvoiceAccount`,
        bodyParams: body,
      });
      return result;
    });
  }
  async importAccountBUs(data: RACAccountBUImportFormData) {
    return await ServiceUtils.toastError(async () => {
      const { ...body } = data;
      const result = await HTTP.post({
        target: "EMPTY",
        url: `/api/invoice/importInvoiceAccountBU`,
        bodyParams: body,
      });
      return result;
    });
  }

  async createBookingReason(data: RACBookingReasonData) {
    return await ServiceUtils.toastError(async () => {
      const result = await SubmitService.submitDataAsync({
        type: "asset",
        assetType: AssetTypes.Invoicing.BookingReason,
        ignorePropChecks: true,
        ignoreSubmitValidation: true,

        data: {
          data: data,
        },
      });
      return result;
    });
  }

  async updateBookingReason(id: ObjectId, data: RACBookingReasonData) {
    return await ServiceUtils.toastError(async () => {
      const result = await SubmitService.submitDataAsync({
        type: "asset",
        assetType: AssetTypes.Invoicing.BookingReason,
        ignorePropChecks: true,
        ignoreSubmitValidation: true,
        id: id,
        data: {
          _id: id,
          data: data,
        },
      });
      return result;
    });
  }
}
const RACService = new RACServiceClass();
export default RACService;
